/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { DataConnected } from '@carbon/react/icons';
import { Button } from 'carbon-components-react';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppSelector } from '../../../../../store';
import { LoaderStates } from '../../../../../ts';
import { FeatureFlag } from '../../../../../ts/feature-flags';
import { selectDraftEnvironmentId } from '../../../../ia/store/environments/selectors';
import { isFeatureEnabled } from '../../../../ia/store/features/selectors';
import { ChannelContentProps } from '../../../util/types';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { CatalogOpenApiMessages } from '../messages';
import { MappingContent } from './MappingContent';
import { MappingStepMessages as messages } from './messages';
import { UpdateCatalogOpenApiSpecModal } from './UpdateCatalogOpenApiSpecModal';

import '../register/CatalogOpenApiOverlay.scss';

export const MappingTab: React.FC<ChannelContentProps> = ({
  catalogItemId,
  environmentId,
  readOnly
}) => {
  const isUpdateExtensionEnabled = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.INTEGRATIONS_CUSTOM_UPDATE)
  );
  const draftEnvironmentId = useAppSelector(selectDraftEnvironmentId);
  const isDraftExtension = environmentId === draftEnvironmentId;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // Updating the Catalog Item's spec body
  const [updatingCatalogItemSpecState, setUpdatingCatalogItemSpecState] =
    useState<string>(LoaderStates.IDLE);

  return (
    <TabContent
      ImageComponent={() => <DataConnected size={32} />}
      title={
        isUpdateExtensionEnabled && isDraftExtension ? (
          <FormattedMessage {...CatalogOpenApiMessages.manageExtension} />
        ) : (
          <FormattedMessage {...messages.title} />
        )
      }>
      <div className="TabContent__body-content">
        <div className="TabContent__subtitle">
          <FormattedMessage {...messages.subtitle} />
        </div>
        {isUpdateExtensionEnabled && isDraftExtension && (
          <>
            <div className="TabContent__subtitle">
              <FormattedMessage {...messages.updateSubtitle} />
            </div>
            <div className="CatalogOpenApiFileImport__replace">
              <Button disabled={readOnly} onClick={() => setIsModalOpen(true)}>
                <FormattedMessage {...messages.replace} />
              </Button>
            </div>
            <div className="TabContent__spacer16" />
          </>
        )}
        <MappingContent
          catalogItemId={catalogItemId}
          updatingCatalogItemSpecState={updatingCatalogItemSpecState}
          setUpdatingCatalogItemSpecState={setUpdatingCatalogItemSpecState}
        />
        {isUpdateExtensionEnabled && isDraftExtension && (
          <UpdateCatalogOpenApiSpecModal
            catalogItemId={catalogItemId}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            setUpdatingCatalogItemSpecState={setUpdatingCatalogItemSpecState}
          />
        )}
      </div>
    </TabContent>
  );
};
